'use client';

import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
    type IdentityUser,
    type UserData,
    type UserDataToUpdate,
    UserSessionProvider,
    type UserSessionProviderContextProps,
    decodeJwtCookie,
    getCookieValue,
    getNextApiRoute,
    useEffectOnce,
    useIsExperimentFullSolution,
    PageType,
} from '@libs/common-next';
import type { Store, UpcomingOrderGrooveDetails } from '@libs/common-node';
import {
    AUTH_COOKIES,
    Locale,
    VELOCITY_LOCAL_STORAGE_KEY,
    getLocale,
    safeJsonParse,
    type Nullable,
} from '@libs/core';
import apiProxyClient from '@libs/api-proxy-client';
import { getAppointments, getPackages } from '@libs/api-proxy-client/v2';
import { AccountAPI } from '@libs/experience-api';
import { initSession } from '@libs/ocapi/server';
import { getOrderGrooveSubscriptionInfo } from '@libs/order-groove/server';
import { useUserSessionLocalStorage } from '../../hooks/useUserSessionLocalStorage';
import { updateStoreGuestUser } from '../StoreDrawer/utils';
import { shouldDeleteLocalStorage } from '../../utils';
import {
    STORE_NUMBER,
    VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM,
    PROFILE_DATA,
} from '../../constants';
import { getStoreById } from './getStoreById';

const refreshOcapi = initSession;

/**
 * This function is required because the same customer is shared
 * for both CA and US and the "storeID" prop is also shared. This
 * behavior is intended to mimic the legacy app. Ideally we should have
 * something like "storeID_CA" and "storeID_US", but this would require
 * extensive changes on the legacy codebase or use a separate Customer List.
 */
function isStoreFromSiteCountry(store: Store) {
    const { CountryAbbreviation } = store;

    const locale = getLocale();
    const currentCountry = locale === Locale['en-US'] ? 'US' : 'CA';

    return CountryAbbreviation === currentCountry;
}

function getVelocityStoreDeletedStorage() {
    return IS_SSR
        ? ''
        : localStorage.getItem(`${VELOCITY_LOCAL_STORAGE_KEY}-store-deleted`);
}

/**
 * @function selectivelyClearStoreData
 * @description SB-277 & SB-406 temporary function to handle persistent incorrect store 0453 | store 2234 and delete the storage once per VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM
 * Update the increment for VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM if this function has been modified
 *
 * TODO: Remove function once we roll off of SFCC and no longer need this preliminary check SB-277
 */
async function selectivelyClearStoreData(
    storedStoreData: Nullable<{
        selectedStore: Nullable<Store>;
        favoriteStore: Nullable<Store>;
    }>,
    isFullSolution: boolean
) {
    const storeNumber = storedStoreData?.selectedStore?.StoreNumber;

    if (
        (isEqual(storeNumber, STORE_NUMBER.STERLING_VA) ||
            isEqual(storeNumber, STORE_NUMBER.NIAGARA_FALLS)) &&
        shouldDeleteLocalStorage(
            getVelocityStoreDeletedStorage(),
            VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM
        )
    ) {
        // remove selected store from Demandware session
        await updateStoreGuestUser({ c_storeID: '0', isFullSolution });
        localStorage.removeItem(`${VELOCITY_LOCAL_STORAGE_KEY}-store`);

        localStorage.setItem(
            `${VELOCITY_LOCAL_STORAGE_KEY}-store-deleted`,
            VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM.toString()
        );
        console.error(
            '[UserSessionWrapper] [selectivelyClearStoreData] ran for store number ',
            storeNumber
        );
    }
}

/**
 * @function shouldInitSetStoreCookie
 * @description SB-338 Handles the initial cookie get and set when the page loads
 * This issue pertains to store number 0453 where it keeps setting this store number even if customer isn't nearby
 *
 * TODO: Remove function once we roll off of SFCC and no longer need this preliminary check SB-338
 */
async function shouldInitSetStoreCookie(
    cookieStoreId: string | null | undefined,
    isFullSolution: boolean
) {
    if (!cookieStoreId) {
        return false;
    }

    if (
        (shouldDeleteLocalStorage(
            getVelocityStoreDeletedStorage(),
            VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM + 1
        ) &&
            isEqual(
                Number(cookieStoreId?.replace(/^0+/, '')),
                STORE_NUMBER.STERLING_VA
            )) ||
        isEqual(
            Number(cookieStoreId?.replace(/^0+/, '')),
            STORE_NUMBER.NIAGARA_FALLS
        )
    ) {
        console.error(
            '[UserSessionWrapper] [shouldInitSetStoreCookie] cleared store for ',
            cookieStoreId
        );
        // remove selected store from Demandware session
        await updateStoreGuestUser({ c_storeID: '0', isFullSolution });
        localStorage.setItem(
            `${VELOCITY_LOCAL_STORAGE_KEY}-store-deleted`,
            (VELOCITY_STORE_DELETED_ATTEMPT_FIX_NUM + 1).toString()
        );
        localStorage.removeItem(`${VELOCITY_LOCAL_STORAGE_KEY}-store`);
        return false;
    }

    return true;
}

function getStorageStore() {
    const storage = IS_SSR
        ? ''
        : localStorage.getItem(`${VELOCITY_LOCAL_STORAGE_KEY}-store`);
    const storedStoreData = safeJsonParse<{
        selectedStore: Nullable<Store>;
        favoriteStore: Nullable<Store>;
    }>(storage);

    if (!storedStoreData) {
        return {
            selectedStore: null,
            favoriteStore: null,
        };
    }

    return {
        ...storedStoreData,
        selectedStore:
            storedStoreData.selectedStore &&
            isStoreFromSiteCountry(storedStoreData.selectedStore)
                ? storedStoreData.selectedStore
                : null,
    };
}

function stripStockData(store: Nullable<Store>) {
    if (!store) {
        return store;
    }

    const {
        HasCurrentProductInStock: _,
        CurrentProductStockstatus: __,
        ...rest
    } = store;

    return rest;
}

function getFavoriteAndSelectedStore(
    storedStoreData: {
        selectedStore: Store | null;
        favoriteStore: Nullable<Store>;
    },
    result: Store | null
) {
    const storedFavoriteStore = storedStoreData?.favoriteStore;
    const storedSelectedStore = storedStoreData?.selectedStore;
    const guestFavoriteStore = isEqual(storedFavoriteStore, result)
        ? storedFavoriteStore ?? result
        : result;
    const guestSelectedStore = isEqual(storedSelectedStore, result)
        ? storedSelectedStore ?? result
        : result;

    return { guestFavoriteStore, guestSelectedStore };
}

const loadingState = {
    isLoading: true,
};

interface PageTypeProps {
    pageType?: Nullable<PageType>;
}

export default function UserSessionWrapper({
    children,
    pageType,
}: PropsWithChildren<PageTypeProps>) {
    const storedStoreData = getStorageStore();
    const isExperimentFullSolution = useIsExperimentFullSolution();

    const [ocapi, setOcapi] =
        useState<UserSessionProviderContextProps['ocapi']>(loadingState);
    const [favoriteStore, setFavoriteStore] = useState<
        UserSessionProviderContextProps['favoriteStore']
    >({
        ...loadingState,
        store: storedStoreData.favoriteStore,
    });
    const [selectedStore, setSelectedStore] = useState<
        UserSessionProviderContextProps['selectedStore']
    >({
        ...loadingState,
        store: storedStoreData.selectedStore,
    });
    const [loyalty, setLoyalty] =
        useState<UserSessionProviderContextProps['loyalty']>();
    const [orderGrooveData, setOrderGrooveData] =
        useState<Nullable<UpcomingOrderGrooveDetails>>();
    const [user, setUser] = useState<UserSessionProviderContextProps['user']>(
        // set initial state
        () => decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN)
    );
    const [userData, setUserData] =
        useState<UserSessionProviderContextProps['userData']>();

    const [isAuthenticated, setIsAuthenticated] =
        useState<UserSessionProviderContextProps['isAuthenticated']>();

    // TODO: remove all ocapi usage after go-live
    const customer = ocapi.data?.session?.customer;
    const isAuthenticatedOcapi = customer?.auth_type === 'registered';
    const basket = ocapi.data?.basket;

    const [storageUserSession] = useUserSessionLocalStorage({
        customer,
        loyalty,
        basket,
    });

    const isCartPage = pageType === PageType.CART;

    const getUserData = useCallback(async () => {
        const [
            addressesResult,
            appointmentsResult,
            packagesResult,
            paymentsResult,
            profileResult,
        ] = await Promise.allSettled(
            isCartPage
                ? [null, null, null, null, AccountAPI.Client.getProfile()]
                : [
                      AccountAPI.Client.getAddresses(),
                      getAppointments(),
                      getPackages(),
                      AccountAPI.Client.getPayments(),
                      AccountAPI.Client.getProfile(),
                  ]
        );

        function getResult<T>(result: PromiseSettledResult<T>) {
            return result.status === 'rejected' ? null : result.value;
        }

        const addresses = getResult(addressesResult);
        const appointments = getResult(appointmentsResult);
        const packages = getResult(packagesResult);
        const payments = getResult(paymentsResult);
        const profile = getResult(profileResult);

        const newUserData = {
            addresses,
            appointments,
            packages,
            payments,
            profile,
        };

        setUserData(prev => ({
            ...(prev as UserData),
            ...(newUserData as UserData),
        }));
    }, [isCartPage]);

    /** Rename it to 'updateUserData' when ready to tackle test coverage */
    const updateProfileData = useCallback(
        async (dataToUpdate?: string) => {
            function updateState(key: string, value: UserDataToUpdate) {
                setUserData(prev => ({
                    ...(prev as UserData),
                    [key]: value,
                }));
            }

            switch (dataToUpdate) {
                case PROFILE_DATA.ADDRESSES: {
                    const addresses = await AccountAPI.Client.getAddresses();
                    return updateState(PROFILE_DATA.ADDRESSES, addresses);
                }
                case PROFILE_DATA.APPOINTMENTS: {
                    const appointments = await getAppointments();
                    return updateState(PROFILE_DATA.APPOINTMENTS, appointments);
                }
                case PROFILE_DATA.PACKAGES: {
                    const packages = await getPackages();
                    return updateState(PROFILE_DATA.PACKAGES, packages);
                }
                case PROFILE_DATA.PAYMENTS: {
                    const payments = await AccountAPI.Client.getPayments();
                    return updateState(PROFILE_DATA.PAYMENTS, payments);
                }
                case PROFILE_DATA.PROFILE: {
                    const profile = await AccountAPI.Client.getProfile();
                    return updateState(PROFILE_DATA.PROFILE, profile);
                }
                default: {
                    return getUserData();
                }
            }
        },
        [getUserData]
    );

    // TODO: Remove this function once we found long-term solution for SB-277
    useEffectOnce(() => {
        selectivelyClearStoreData(storedStoreData, isExperimentFullSolution);
    });

    // TODO: remove ocapi conditions after go-live
    useEffect(() => {
        async function sessionInit() {
            try {
                const url = getNextApiRoute('session');
                await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
            } catch (e) {
                console.debug('failed dw cookie sync', e); // HCP-794 change error entry to console instead of sentry as this is not imacting user workflow
            }

            const result = await initSession({ isExperimentFullSolution });
            setOcapi({
                isLoading: false,
                data: result,
            });
        }

        if (!isExperimentFullSolution) {
            sessionInit();
        } else {
            setOcapi({
                isLoading: false,
                data: null,
            });
        }
    }, [isExperimentFullSolution]);

    // Set initial store data (based on localStorage or Cookie/API)
    useEffect(() => {
        async function storeInit() {
            const storeId = customer?.c_storeID;
            // TODO: Update logic once rolling off of SFCC so we don't have to do preliminary check SB-338
            const cookieStoreId = (await shouldInitSetStoreCookie(
                getCookieValue('StoreCookie'),
                isExperimentFullSolution
            ))
                ? getCookieValue('StoreCookie')
                : null;

            const storeParam = storeId ?? cookieStoreId;

            const result = storeParam ? await getStoreById(storeParam) : null;

            // TODO: remove ocapi if condition after go-live
            if (isAuthenticatedOcapi) {
                const loadedStore = {
                    isLoading: false,
                    store: result,
                };

                if (result && isStoreFromSiteCountry(result)) {
                    setSelectedStore(loadedStore);
                } else {
                    // Let the default value takeover.
                    setSelectedStore(store => ({
                        ...store,
                        isLoading: false,
                    }));
                }

                // This was done to mimic the legacy behavior,
                // which loads it as favorite no matter the locale
                setFavoriteStore(loadedStore);
            } else {
                const guestSavedStore = getFavoriteAndSelectedStore(
                    storedStoreData,
                    result
                );
                setFavoriteStore({
                    isLoading: false,
                    store: stripStockData(guestSavedStore.guestFavoriteStore),
                });
                setSelectedStore({
                    isLoading: false,
                    store: stripStockData(guestSavedStore.guestSelectedStore),
                });
            }
        }

        if (!ocapi.isLoading) {
            storeInit();
        }
    }, [customer?.c_storeID, ocapi.isLoading]);

    // Save store data to storage for future use (on app switch)
    useEffect(() => {
        const storeData = safeJsonParse<{
            selectedStore: Nullable<Store>;
            favoriteStore: Nullable<Store>;
        }>(localStorage.getItem(`${VELOCITY_LOCAL_STORAGE_KEY}-store`));

        if (
            !isEqual(
                storeData?.selectedStore,
                stripStockData(selectedStore.store)
            ) ||
            !isEqual(
                storeData?.favoriteStore,
                stripStockData(favoriteStore.store)
            )
        ) {
            localStorage.setItem(
                `${VELOCITY_LOCAL_STORAGE_KEY}-store`,
                JSON.stringify({
                    selectedStore: stripStockData(selectedStore.store),
                    favoriteStore: stripStockData(
                        favoriteStore.store || storeData?.favoriteStore
                    ),
                })
            );
        }
    }, [favoriteStore, selectedStore]);

    // TODO: remove effect after go-live
    useEffect(() => {
        async function loyaltyInit() {
            if (customer?.c_petmToken) {
                const result = await apiProxyClient.loyalty.getLoyaltyPoints(
                    customer?.c_petmToken
                );
                setLoyalty(result);
            }
        }

        loyaltyInit();
    }, [customer?.c_petmToken]);

    useEffect(() => {
        async function orderGrooveInit() {
            if (isAuthenticated) {
                const result = await getOrderGrooveSubscriptionInfo(
                    isAuthenticated === 'velocity'
                        ? user?.sid
                        : customer?.c_janrainIdentifier // as the sid of the new implemented user session is the same as c_janrainIdentifier, we're checking if user logged in with new experience passing sid if not the legacy identifier
                );
                setOrderGrooveData(result);
            }
        }

        orderGrooveInit();
    }, [customer?.c_janrainIdentifier, isAuthenticated, user?.sid]);

    useEffect(() => {
        if (user) {
            getUserData();
        }
    }, [user, getUserData]);

    useEffect(() => {
        if (user) {
            setIsAuthenticated('velocity');
        } else if (isAuthenticatedOcapi) {
            setIsAuthenticated('ocapi');
        } else {
            setIsAuthenticated(undefined);
        }
    }, [isAuthenticatedOcapi, user]);

    const value = useMemo(
        () => ({
            favoriteStore,
            isAuthenticated,
            loyalty,
            ocapi,
            orderGrooveData,
            refreshOcapi,
            selectedStore,
            setFavoriteStore,
            setOcapi,
            setSelectedStore,
            setUser,
            storageUserSession,
            user,
            userData,
            updateProfileData,
            addAddress: AccountAPI.addAddress,
            editAddress: AccountAPI.updateAddress,
        }),
        [
            favoriteStore,
            isAuthenticated,
            loyalty,
            ocapi,
            orderGrooveData,
            selectedStore,
            storageUserSession,
            user,
            userData,
            updateProfileData,
        ]
    );

    return <UserSessionProvider values={value}>{children}</UserSessionProvider>;
}
